import type { Offer } from '@ooh/contracts';
import type { Badge } from '@ooh/ui/components/badge';
import type React from 'react';

type OfferStatusLabelKey =
  | 'status.DRAFT'
  | 'status.SENT'
  | 'status.ACCEPTED'
  | 'status.CONFIRMED'
  | 'status.SOLD'
  | 'status.EXPIRED'
  | 'status.CANCELLED';

export function getOfferStatusLabelKey(status: Offer['status']): OfferStatusLabelKey {
  switch (status) {
    case 'DRAFT':
      return 'status.DRAFT' as const;
    case 'SENT':
      return 'status.SENT' as const;
    case 'ACCEPTED':
      return 'status.ACCEPTED' as const;
    case 'CONFIRMED':
      return 'status.CONFIRMED' as const;
    case 'SOLD':
      return 'status.SOLD' as const;
    case 'EXPIRED':
      return 'status.EXPIRED' as const;
    case 'CANCELLED':
      return 'status.CANCELLED' as const;
  }

  // If Offer['status'] is extended, the switch above will stop being exhaustive
  // and this line will surface the unhandled value at runtime.
  throw new Error(`Unhandled offer status: ${String(status)}`);
}

export function getOfferStatusVariant(
  status: Offer['status'],
): React.ComponentProps<typeof Badge>['variant'] {
  switch (status) {
    case 'DRAFT':
      return 'outline';
    case 'SENT':
      return 'info';
    // ACCEPTED (client accepted, awaiting internal confirmation) and CONFIRMED
    // (internal approval granted — final state) previously shared the same
    // green "success" badge, making them visually indistinguishable
    // (CU-86caypxeh). ACCEPTED now uses "warning" (amber) as the in-progress
    // color; CONFIRMED keeps "success" (green) as the terminal/done color.
    case 'ACCEPTED':
      return 'warning';
    case 'CONFIRMED':
      return 'success';
    // SOLD must be visually distinct from CONFIRMED's "success" green (campaign-sale /
    // "Sold campaign state") — "secondary" (neutral gray) is otherwise unused among
    // these statuses, unlike destructive/warning/info/success which are already spoken
    // for.
    case 'SOLD':
      return 'secondary';
    case 'EXPIRED':
      return 'destructive';
    // CANCELLED reuses the same "terminal/negative" destructive variant as EXPIRED —
    // the two are distinguished by their label text ("Anulowana" vs "Wygasla"), not color.
    case 'CANCELLED':
      return 'destructive';
  }

  // If Offer['status'] is extended, the switch above will stop being exhaustive
  // and this line will surface the unhandled value at runtime.
  throw new Error(`Unhandled offer status: ${String(status)}`);
}
